import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import { adminSession } from '@/lib/api';
import { friendlyError } from '@/lib/errors';

/**
 * /dashboard/health — operational snapshot.
 *
 * Six panels, all backed by a single reactive useQuery against
 * api.admin.health.health. Convex pushes fresh values whenever any
 * watched row changes — no polling loop needed.
 *
 *   1. DB ping (green/red dot + ms)
 *   2. River queue depth by state — tiles
 *   3. Per-cron last successful finalize ts — table
 *   4. Recent failures with kind + last_error — table
 *   5. Recent webhook ingest (Stripe + RC) — table
 *   6. Approximate DB row counts on the hot tables
 */
export function HealthPage() {
  return (
    <HealthErrorBoundary>
      <HealthInner />
    </HealthErrorBoundary>
  );
}

function HealthInner() {
  const token = adminSession.getToken() ?? '';
  const health = useQuery(api.admin.health.health, { token });
  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
        <div>
          <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
            System health
          </h2>
          <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 4 }}>
            Live — updates as data changes.
            {health && (
              <span style={{ marginLeft: 8 }}>
                Last sample: {new Date(health.now).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </header>

      {!health ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>
          Loading…
        </div>
      ) : (
        <>
          {/* DB ping tile */}
          <Section title="Database">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  aria-hidden
                  style={{
                    width: 10, height: 10, borderRadius: 10,
                    background: health.db_ok ? '#2e7d32' : '#a8331f',
                    display: 'inline-block',
                  }}
                />
                <span style={{ fontWeight: 600 }}>
                  {health.db_ok ? 'Reachable' : 'Unreachable'}
                </span>
              </div>
              <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                ping {health.db_ping_ms ?? '—'} ms
              </span>
            </div>
            {health.db_row_counts_approx && (
              <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
                {Object.entries(health.db_row_counts_approx).map(([k, v]) => (
                  <CountChip key={k} label={k} value={v.toLocaleString()} />
                ))}
              </div>
            )}
          </Section>

          {/* Job queue tiles */}
          <Section title="Job queue (River)">
            {!health.jobs || Object.keys(health.jobs).length === 0 ? (
              <Hint>No jobs in any state.</Hint>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                {(['available', 'running', 'retryable', 'discarded', 'scheduled', 'pending', 'completed', 'cancelled'] as const).map((state) => {
                  const n = health.jobs?.[state] ?? 0;
                  const tone =
                    state === 'discarded' && n > 0 ? 'warn' :
                    state === 'retryable' && n > 0 ? 'warn' :
                    'neutral';
                  return <StateTile key={state} label={state} value={n} tone={tone} />;
                })}
              </div>
            )}
          </Section>

          {/* Registered crons + their schedule. (Convex's scheduler doesn't
              expose per-cron last-run timestamps to a query, so we show the
              schedule rather than a fake "last run" time.) */}
          <Section title="Scheduled jobs">
            {!health.crons?.length ? (
              <Hint>No scheduled jobs registered.</Hint>
            ) : (
              <table style={tbl}>
                <thead>
                  <tr>
                    <th style={th}>Kind</th>
                    <th style={th}>Schedule</th>
                  </tr>
                </thead>
                <tbody>
                  {health.crons.map((c) => (
                    <tr key={c.kind}>
                      <td style={td}><span className="mono">{c.kind}</span></td>
                      <td style={td}>{c.schedule}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Recent failures */}
          <Section title="Recent failures (retryable + discarded)">
            {!health.jobs_recent_failed?.length ? (
              <Hint style={{ color: '#2e7d32' }}>No recent failures.</Hint>
            ) : (
              <table style={tbl}>
                <thead>
                  <tr>
                    <th style={th}>Kind</th>
                    <th style={th}>State</th>
                    <th style={th}>Attempt</th>
                    <th style={th}>Last error</th>
                    <th style={th}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {health.jobs_recent_failed.map((j) => (
                    <tr key={j.id}>
                      <td style={td}><span className="mono">{j.kind}</span></td>
                      <td style={td}><StateBadge state={j.state} /></td>
                      <td style={td}><span className="mono">{j.attempt}/{j.max_attempts}</span></td>
                      <td style={{ ...td, maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span className="mono" style={{ fontSize: 11, color: '#a8331f' }}>
                          {j.last_error || '—'}
                        </span>
                      </td>
                      <td style={td}>
                        {j.attempted_at ? new Date(j.attempted_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Recent webhooks */}
          <Section title="Recent payment webhooks">
            {!health.webhooks_recent?.length ? (
              <Hint>No webhook events recorded yet.</Hint>
            ) : (
              <table style={tbl}>
                <thead>
                  <tr>
                    <th style={th}>Vendor</th>
                    <th style={th}>Event id</th>
                    <th style={th}>Received</th>
                  </tr>
                </thead>
                <tbody>
                  {health.webhooks_recent.map((ev) => (
                    <tr key={ev.vendor + ev.vendor_event_id}>
                      <td style={td}><span className="mono">{ev.vendor}</span></td>
                      <td style={td}><span className="mono" style={{ fontSize: 11 }}>{ev.vendor_event_id}</span></td>
                      <td style={td}>{new Date(ev.received_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 16 }}>
        {children}
      </div>
    </section>
  );
}

function Hint({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ color: 'var(--ink-3)', fontSize: 13, ...style }}>{children}</div>;
}

function StateTile({ label, value, tone }: { label: string; value: number; tone: 'neutral' | 'warn' }) {
  const warn = tone === 'warn';
  return (
    <div
      style={{
        background: warn ? 'rgba(168,51,31,0.06)' : 'var(--bg)',
        border: '1px solid',
        borderColor: warn ? 'rgba(168,51,31,0.25)' : 'var(--line)',
        borderRadius: 10,
        padding: '10px 12px',
      }}
    >
      <div className="mono" style={{ fontSize: 10, color: warn ? '#a8331f' : 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: warn ? '#a8331f' : 'var(--ink)', marginTop: 2 }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: string }) {
  const color =
    state === 'discarded' ? '#a8331f' :
    state === 'retryable' ? '#a86b00' :
    'var(--ink-3)';
  return (
    <span
      className="mono"
      style={{
        background: 'var(--chip)',
        color,
        fontSize: 10,
        padding: '2px 8px',
        borderRadius: 100,
        textTransform: 'uppercase',
        letterSpacing: 1,
      }}
    >
      {state}
    </span>
  );
}

function CountChip({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        gap: 8,
        alignItems: 'baseline',
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: '8px 12px',
      }}
    >
      <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontWeight: 700 }}>{value}</span>
    </div>
  );
}

const tbl: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  borderBottom: '1px solid var(--line-strong)',
  fontWeight: 600,
  color: 'var(--ink-3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 1,
};
const td: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--line)',
  color: 'var(--ink)',
};

// useQuery re-throws on every render once Convex surfaces an error,
// so the only safe inline-error path is a class ErrorBoundary. Keep
// this local to the page — the root boundary in main.tsx falls back
// to a generic "Something went wrong" string and we want the per-
// route friendlyError() copy here instead.
interface HealthErrorState {
  err: unknown | null;
}

class HealthErrorBoundary extends Component<{ children: ReactNode }, HealthErrorState> {
  state: HealthErrorState = { err: null };
  static getDerivedStateFromError(err: unknown): HealthErrorState {
    return { err };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[HealthPage]', error, info);
  }
  render() {
    if (this.state.err !== null) {
      return (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '14px 16px', borderRadius: 12, fontSize: 13 }}>
          {friendlyError(this.state.err, 'getHealth')}
        </div>
      );
    }
    return this.props.children;
  }
}
